import createDocumentSlice from "./createDocumentSlice";
import { config } from "../../utils/apiUrl";

/*
|--------------------------------------------------------------------------
| SALE INVOICE MODULE
|--------------------------------------------------------------------------
*/

const saleInvoiceModule =
    createDocumentSlice({
        sliceName:
            "saleInvoice",

        stateKey:
            "saleInvoice",

        documentName:
            "Sale Invoice",

        documentType:
            "SALE_INVOICE",

        documentKey:
            "saleInvoice",

        documentKeys: [
            "invoice",
            "sourceSaleInvoice",
        ],

        listKey:
            "saleInvoices",

        listKeys: [
            "invoices",
        ],

        urls: {
            list:
                config.getSaleInvoices,

            summary:
                config.getSaleInvoiceSummary,

            createData:
                config.getSaleInvoiceCreateData,

            dropdowns:
                config.getSaleInvoiceDropdowns,

            getById:
                config.getSaleInvoiceById,

            create:
                config.createSaleInvoice,

            update:
                config.updateSaleInvoice,

            confirm:
                config.confirmSaleInvoice,

            cancel:
                config.cancelSaleInvoice,

            duplicate:
                config.duplicateSaleInvoice,

            /*
            |--------------------------------------------------------------------------
            | SALE INVOICE CONVERSION
            |--------------------------------------------------------------------------
            |
            | Sale invoices normally do not convert to another sales document.
            | Returns and credit notes use their own APIs.
            |--------------------------------------------------------------------------
            */

            convert:
                null,

            delete:
                config.deleteSaleInvoice,

            history:
                config.getSaleInvoiceHistory,
        },

        initialFilters: {
            paymentStatus:
                "",

            fulfillmentStatus:
                "",

            conversionStatus:
                "",

            returnStatus:
                "",

            overdueOnly:
                false,
        },

        initialSummary: {
            unpaidCount:
                0,

            partiallyPaidCount:
                0,

            paidCount:
                0,

            overdueCount:
                0,

            overdueAmount:
                0,

            returnedCount:
                0,

            partiallyReturnedCount:
                0,

            fullyReturnedCount:
                0,

            storeSaleAmount:
                0,

            onlineSaleAmount:
                0,

            todaySaleAmount:
                0,

            todayInvoiceCount:
                0,
        },

        initialDropdowns: {
            documentStatuses:
                [],

            paymentStatuses:
                [],

            fulfillmentStatuses:
                [],

            returnStatuses: [
                "NOT_RETURNED",
                "PARTIALLY_RETURNED",
                "FULLY_RETURNED",
            ],

            salesChannels:
                [],

            priceModes:
                [],

            discountTypes:
                [],
        },

        supportsConfirm:
            true,

        supportsCancel:
            true,

        supportsDuplicate:
            true,

        supportsConvert:
            false,

        supportsDelete:
            true,

        supportsHistory:
            true,
    });

/*
|--------------------------------------------------------------------------
| REDUCER
|--------------------------------------------------------------------------
*/

export default
    saleInvoiceModule.reducer;

/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {
    setFilters:
    setSaleInvoiceFilters,

    setFilter:
    setSaleInvoiceFilter,

    setPage:
    setSaleInvoicePage,

    setLimit:
    setSaleInvoiceLimit,

    resetFilters:
    resetSaleInvoiceFilters,

    setSelectedDocument:
    setSelectedSaleInvoice,

    clearSelectedDocument:
    clearSelectedSaleInvoice,

    clearHistory:
    clearSaleInvoiceHistory,

    clearErrors:
    clearSaleInvoiceErrors,

    clearOperation:
    clearSaleInvoiceOperation,

    clearLastCreatedDocument:
    clearLastCreatedSaleInvoice,

    clearLastDuplicatedDocument:
    clearLastDuplicatedSaleInvoice,

    clearLastConvertedDocument:
    clearLastConvertedSaleInvoiceDocument,

    resetState:
    resetSaleInvoiceState,
} =
    saleInvoiceModule.actions;

/*
|--------------------------------------------------------------------------
| THUNKS
|--------------------------------------------------------------------------
*/

export const {
    fetchDocuments:
    fetchSaleInvoices,

    fetchSummary:
    fetchSaleInvoiceSummary,

    fetchCreateData:
    fetchSaleInvoiceCreateData,

    fetchDropdowns:
    fetchSaleInvoiceDropdowns,

    fetchDocumentById:
    fetchSaleInvoiceById,

    createDocument:
    createSaleInvoice,

    updateDocument:
    updateSaleInvoice,

    confirmDocument:
    confirmSaleInvoice,

    cancelDocument:
    cancelSaleInvoice,

    duplicateDocument:
    duplicateSaleInvoice,

    /*
    |--------------------------------------------------------------------------
    | convertDocument is intentionally not exported.
    |--------------------------------------------------------------------------
    */

    deleteDocument:
    deleteSaleInvoice,

    fetchDocumentHistory:
    fetchSaleInvoiceHistory,
} =
    saleInvoiceModule.thunks;

/*
|--------------------------------------------------------------------------
| SELECTORS
|--------------------------------------------------------------------------
*/

export const {
    selectModule:
    saleInvoiceSelector,

    selectDocuments:
    saleInvoiceListSelector,

    selectSelectedDocument:
    selectedSaleInvoiceSelector,

    selectSummary:
    saleInvoiceSummarySelector,

    selectFilters:
    saleInvoiceFiltersSelector,

    selectPagination:
    saleInvoicePaginationSelector,

    selectCreateData:
    saleInvoiceCreateDataSelector,

    selectDropdowns:
    saleInvoiceDropdownsSelector,

    selectHistory:
    saleInvoiceHistorySelector,

    selectLoading:
    saleInvoiceLoadingSelector,

    selectOperation:
    saleInvoiceOperationSelector,
} =
    saleInvoiceModule.selectors;

/*
|--------------------------------------------------------------------------
| SPECIALIZED SELECTORS
|--------------------------------------------------------------------------
*/

export const pendingSaleInvoicesSelector =
    (
        state
    ) => {
        const invoices =
            saleInvoiceListSelector(
                state
            );

        return invoices.filter(
            (
                invoice
            ) =>
                [
                    "UNPAID",
                    "PARTIALLY_PAID",
                ].includes(
                    invoice
                        ?.paymentStatus
                )
        );
    };

export const overdueSaleInvoicesSelector =
    (
        state
    ) => {
        const invoices =
            saleInvoiceListSelector(
                state
            );

        const today =
            new Date();

        today.setHours(
            0,
            0,
            0,
            0
        );

        return invoices.filter(
            (
                invoice
            ) => {
                if (
                    !invoice?.dueDate ||
                    ![
                        "UNPAID",
                        "PARTIALLY_PAID",
                    ].includes(
                        invoice
                            ?.paymentStatus
                    )
                ) {
                    return false;
                }

                const dueDate =
                    new Date(
                        invoice.dueDate
                    );

                dueDate.setHours(
                    0,
                    0,
                    0,
                    0
                );

                return (
                    dueDate <
                    today
                );
            }
        );
    };

export const onlineSaleInvoicesSelector =
    (
        state
    ) => {
        const invoices =
            saleInvoiceListSelector(
                state
            );

        return invoices.filter(
            (
                invoice
            ) =>
                invoice
                    ?.salesChannel ===
                "ONLINE" ||
                invoice
                    ?.sourceType ===
                "ONLINE_ORDER"
        );
    };

export const storeSaleInvoicesSelector =
    (
        state
    ) => {
        const invoices =
            saleInvoiceListSelector(
                state
            );

        return invoices.filter(
            (
                invoice
            ) =>
                invoice
                    ?.salesChannel ===
                "STORE"
        );
    };

export const returnedSaleInvoicesSelector =
    (
        state
    ) => {
        const invoices =
            saleInvoiceListSelector(
                state
            );

        return invoices.filter(
            (
                invoice
            ) =>
                [
                    "PARTIALLY_RETURNED",
                    "FULLY_RETURNED",
                ].includes(
                    invoice
                        ?.returnStatus
                )
        );
    };